import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Tooltip } from 'reactstrap';
import ActionItem from './ActionItem';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { usePermissions } from 'Components/Hooks/usePermissions';

interface WithId { id: string | number }

interface Permission {
    access: boolean;
    type: 'lock' | string;
    help?: string;
}

interface ExtraAction<T> {
    btn_icon: string;
    btn_type: string;
    btn_name: string;
    clickHandler: (row: T) => void;
    conditionalField?: keyof T,
    conditionalFieldValue?: any,
    action?: any;
    module?: any;
    skipPermission?:any;
    hidePermission?: boolean;
}

interface DropdownActionsProps<T extends WithId> {
    row: { original: T };
    url?: string;
    permissions: {
        view?: Permission;
        edit?: Permission;
        delete?: Permission;
    };
    extraActions?: ExtraAction<T>[];
    handleLeadClick: (row: T) => void;
    setIsViewAction?: (value: boolean) => void;
    setIsEditAction?: (value: boolean) => void;
    setIsDeleteOpen?: (value: boolean) => void;
    isViewDrawer?: boolean;
    isEditDrawer?: boolean;
    translater: (key: string) => string;
    actionInUrl?: boolean;
    actionArray?: any;
    module?: any;
    skipPermission?:any;
}

export const DropdownActions = <T extends WithId>({
    row,
    url,
    permissions,
    extraActions = [],
    handleLeadClick,
    setIsViewAction,
    setIsEditAction,
    setIsDeleteOpen,
    isViewDrawer = false,
    isEditDrawer = false,
    translater,
    actionInUrl,
    actionArray,
    module,
    skipPermission
}: DropdownActionsProps<T>) => {
    const navigate = useNavigate();
    const { checkModulePermission } = usePermissions();
    const itemId = String(row.original.id);
    const [open, setOpen] = useState<string | null>(null);    
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggle = () => setTooltipOpen(prev => !prev);
    const toggleDropdown = () => {
        setOpen(prev => (prev === itemId ? null : itemId));
    }
   const modulePermission = (module: any, action: any, skipPermission: any) => {
        let moduleOption =module;
        let actionOption =action
        let skipPermissionOption=skipPermission??false
        let perm: any = checkModulePermission(
        moduleOption,
        actionOption,          
        skipPermissionOption
        );
        return {
        access: perm?.allowed,
        type: perm?.locked ? 'lock' : '',
        help: perm?.reason
        };
    }
    const renderAction = (
        perm: Permission | undefined,
        drawer: boolean,
        icon: string,
        labelKey: string,
        btnClass: string,
        onDrawerClick: () => void,
        redirectPath?: string,
    ) => {
        if (actionArray?.length && !actionArray?.includes(labelKey)) return null
        if(!perm) {
            perm = modulePermission(module, labelKey, skipPermission)
        }
        if (!perm) return null;
        const tooltipId = `${labelKey}-${itemId}`;
        const handler = drawer && perm?.access
            ? onDrawerClick
            : perm?.access && redirectPath
                ? () => navigate(redirectPath)
                : () => { };

        return (
            <ActionItem
                permission={perm}
                icon={icon}
                label={translater(`common.table.${labelKey}`)}
                btnClassName={btnClass}
                tooltipId={tooltipId}
                tooltipMessage={perm?.help}
                onClick={handler}
            />
        );
    };
    return (
        <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
                <Dropdown isOpen={open === itemId} toggle={() => toggleDropdown()}>
                    <DropdownToggle className="btn btn-soft-primary btn-sm" tag="button">
                        <i className="ri-more-fill" />
                    </DropdownToggle>
                    <DropdownMenu container="body" className="dropdown-menu-center" style={{zIndex: 9999}}>
                        <div className="d-flex flex-column">
                            {renderAction(
                                permissions?.view,
                                isViewDrawer,
                                'ri-eye-fill',
                                'view',
                                'btn-soft-info',
                                () => {
                                    handleLeadClick(row?.original);
                                    setIsViewAction?.(true);
                                },
                                url ? `${url}/${itemId}` : undefined
                            )}
                            {renderAction(
                                permissions?.edit,
                                isEditDrawer,
                                'ri-pencil-fill',
                                'edit',
                                'btn-soft-secondary',
                                () => {
                                    handleLeadClick(row?.original);
                                    setIsEditAction?.(true);
                                },
                                url ? actionInUrl ? `${url}/edit/${itemId}` : `${url}/${itemId}` : undefined
                            )}
                            {extraActions && extraActions.map((action, idx) => {
                                let labelKey: keyof typeof permissions = action?.action; // for example
                                let skipPermission=action?.skipPermission ?? false
                                let perm = permissions?.[labelKey];
                                if (!permissions?.[labelKey] && !skipPermission) {
                                  perm = modulePermission(module, labelKey, skipPermission);
                                }
                                if(skipPermission) {
                                    perm  = {access: true, type: ''}
                                }
                                const value = action?.conditionalField ? row?.original?.[action?.conditionalField] : null;
                                let disabled=action?.conditionalField && value === action?.conditionalFieldValue || !perm?.access
                                if((!perm?.access || disabled) && action?.hidePermission) return
                                    return <div 
                                    id={`tooltip-${idx}`}><DropdownItem
                                    key={idx}
                                    disabled={disabled}
                                    className={"dropdown-item fs-12 hstack justify-content-between gap-2"}
                                    onClick={() => action.clickHandler(row.original)}
                                    style={{
                                        opacity: (!skipPermission && perm?.access) || !disabled ? 1 : 0.5,
                                        cursor: (!skipPermission && perm?.access)  || !disabled ? 'pointer' : 'not-allowed',
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        {action?.btn_icon ? <button className={`btn btn-sm ${action?.btn_type}`}
                                           style={{
                                            opacity: (!skipPermission && perm?.access) || !disabled ? 1 : 0.5,
                                            cursor: (!skipPermission && perm?.access)  || !disabled ? 'pointer' : 'not-allowed',
                                        }}
                                        >
                                            <i className={action?.btn_icon} />
                                        </button> : null}
                                        <span className="text-primary">{action?.btn_name}</span>
                                    </div>
                                    {perm && !perm.access && perm.type === 'lock' && <i className="ri-lock-2-fill fs-18" />}
                                </DropdownItem>
                                {perm && !perm.access && (
                                    <Tooltip
                                        placement="top"
                                        isOpen={tooltipOpen}
                                        target={`tooltip-${idx}`}
                                        toggle={toggle} 
                                        style={{zIndex: 10000}}
                                    >
                                        {perm?.help}
                                    </Tooltip>
                                )}
                                </div>
                            })}
                            {renderAction(
                                permissions?.delete,
                                true,
                                'ri-delete-bin-fill',
                                'delete',
                                'btn-soft-danger',
                                () => {
                                    handleLeadClick && handleLeadClick(row?.original);
                                    setIsDeleteOpen?.(true);
                                }
                            )}
                        </div>
                    </DropdownMenu>
                </Dropdown>
            </li>
        </ul>
    );
};
